import { BusinessDetails } from "@/types/index";

// Extended business details interface with shift self-assignment and time tracking settings
export interface ExtendedBusinessDetails extends BusinessDetails {
  // Branding
  favicon_url?: string | null;

  // Shift self-assignment settings
  shift_request_advance_hours?: number;
  shift_request_deadline_days?: number;
  auto_approve_requests?: boolean;
  manager_notification_mode?: string;
  request_expiry_hours?: number;
  max_pending_requests?: number;
  max_requests_per_day?: number;
  require_request_notes?: boolean;
  enable_employee_notifications?: boolean;
  enable_manager_email_notifications?: boolean;
  request_priority_mode?: string;
  enable_shift_swapping?: boolean;
  require_swap_approval?: boolean;
  swap_advance_hours?: number;
  // Historical edit protection
  historical_edit_grace_days?: number;

  // Time tracking business rules
  late_arrival_threshold_minutes?: number;
  grace_period_minutes?: number;
  auto_missed_after_minutes?: number;
  early_arrival_threshold_minutes?: number;
  early_departure_threshold_minutes?: number;
  enable_overtime_tracking?: boolean;
  overtime_threshold_minutes?: number;
  require_arrival_confirmation?: boolean;
}

// Basic business details for the main form
export interface BusinessBasicDetails {
  name: string;
  address: string;
  website: string;
  phone: string;
  google_place_id: string;
  sender_name: string;
  reply_to: string;
  latitude: string;
  longitude: string;
  // Social media links
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  linkedin_url: string;
  youtube_url: string;
}

// Shift settings for the shift form
export interface ShiftAssignmentSettings {
  allow_shift_self_assignment: boolean;
  shift_request_advance_hours: number;
  shift_request_deadline_days: number;
  auto_approve_requests: boolean;
  manager_notification_mode: string;
  request_expiry_hours: number;
  max_pending_requests: number;
  max_requests_per_day: number;
  require_request_notes: boolean;
  enable_employee_notifications: boolean;
  enable_manager_email_notifications: boolean;
  request_priority_mode: string;
  enable_shift_swapping: boolean;
  require_swap_approval: boolean;
  swap_advance_hours: number;
  // Historical edit protection
  historical_edit_grace_days: number;
}

// Time tracking settings for the time tracking form
export interface TimeTrackingSettings {
  late_arrival_threshold_minutes: number;
  grace_period_minutes: number;
  auto_missed_after_minutes: number;
  early_arrival_threshold_minutes: number;
  early_departure_threshold_minutes: number;
  enable_overtime_tracking: boolean;
  overtime_threshold_minutes: number;
  require_arrival_confirmation: boolean;
}

/**
 * Creates default values for business basic details form
 */
export const createBusinessDefaults = (businessDetails: ExtendedBusinessDetails): BusinessBasicDetails => ({
  name: businessDetails.name || "",
  address: businessDetails.address || "",
  website: businessDetails.website || "",
  phone: businessDetails.phone || "",
  google_place_id: businessDetails.google_place_id || "",
  sender_name: (businessDetails as any).sender_name || businessDetails.name || "",
  reply_to: (businessDetails as any).reply_to || "",
  latitude: (businessDetails as any).latitude?.toString() || "",
  longitude: (businessDetails as any).longitude?.toString() || "",
  // Social media links
  facebook_url: (businessDetails as any).facebook_url || "",
  instagram_url: (businessDetails as any).instagram_url || "",
  twitter_url: (businessDetails as any).twitter_url || "",
  linkedin_url: (businessDetails as any).linkedin_url || "",
  youtube_url: (businessDetails as any).youtube_url || "",
});

/**
 * Creates default values for shift assignment settings form
 */
export const createShiftDefaults = (businessDetails: ExtendedBusinessDetails): ShiftAssignmentSettings => ({
  allow_shift_self_assignment: businessDetails.allow_shift_self_assignment || false,
  shift_request_advance_hours: businessDetails.shift_request_advance_hours || 2,
  shift_request_deadline_days: businessDetails.shift_request_deadline_days || 14,
  auto_approve_requests: businessDetails.auto_approve_requests || false,
  manager_notification_mode: businessDetails.manager_notification_mode || "immediate",
  request_expiry_hours: businessDetails.request_expiry_hours || 48,
  max_pending_requests: businessDetails.max_pending_requests || 5,
  max_requests_per_day: businessDetails.max_requests_per_day || 3,
  require_request_notes: businessDetails.require_request_notes || false,
  enable_employee_notifications: businessDetails.enable_employee_notifications ?? true,
  enable_manager_email_notifications: businessDetails.enable_manager_email_notifications ?? true,
  request_priority_mode: businessDetails.request_priority_mode || "first-come",
  enable_shift_swapping: businessDetails.enable_shift_swapping || false,
  require_swap_approval: businessDetails.require_swap_approval ?? true,
  swap_advance_hours: businessDetails.swap_advance_hours || 24,
  historical_edit_grace_days: businessDetails.historical_edit_grace_days ?? 3,
});

/**
 * Creates default values for time tracking settings form
 */
export const createTimeTrackingDefaults = (businessDetails: ExtendedBusinessDetails): TimeTrackingSettings => ({
  late_arrival_threshold_minutes: businessDetails.late_arrival_threshold_minutes || 5,
  grace_period_minutes: businessDetails.grace_period_minutes || 15,
  auto_missed_after_minutes: businessDetails.auto_missed_after_minutes ?? 0,
  early_arrival_threshold_minutes: businessDetails.early_arrival_threshold_minutes || 30,
  early_departure_threshold_minutes: businessDetails.early_departure_threshold_minutes || 15,
  enable_overtime_tracking: businessDetails.enable_overtime_tracking ?? true,
  overtime_threshold_minutes: businessDetails.overtime_threshold_minutes || 15,
  require_arrival_confirmation: businessDetails.require_arrival_confirmation ?? true,
});

/**
 * Creates default values for the complete form (for backwards compatibility)
 */
export const createCompleteFormDefaults = (businessDetails: ExtendedBusinessDetails) => ({
  ...createBusinessDefaults(businessDetails),
  ...createShiftDefaults(businessDetails),
  ...createTimeTrackingDefaults(businessDetails),
});